import { useState, useEffect } from 'react'
import { useToast } from '../../context/ToastContext'
import { useExecutions } from '../../context/ExecutionContext'
import { useAuth } from '../../context/AuthContext'
import { InfoTip } from '../../components/InfoTip'
import { API_BASE } from '../../utils/api'
import { MobileCard, MobileOnly, DesktopOnly, MobileKpi } from '../../components/MobileCard'

function fmtINR(v: number): string {
  const abs = Math.abs(v)
  const s = abs >= 1e7 ? `₹${(abs / 1e7).toFixed(1)}Cr`
          : abs >= 1e5 ? `₹${(abs / 1e5).toFixed(1)}L`
          : `₹${abs.toLocaleString('en-IN')}`
  return v < 0 ? `-${s}` : `+${s}`
}

interface PanicModal {
  level: 'set' | 'strategy' | 'broker' | 'trader' | 'all'
  targetName: string
}

export function AdminDashboard({ onNavigate }: { onNavigate: (v: any) => void }) {
  const [selectedUser, setSelectedUser] = useState('all')
  const [selectedBroker, setSelectedBroker] = useState('all')
  const [panic, setPanic] = useState<PanicModal | null>(null)
  const [dashData, setDashData] = useState<any>(null)
  const [loading, setLoading] = useState(true)
  const [cbSubmitting, setCbSubmitting] = useState(false)
  const { addToast } = useToast()
  const { panicExit } = useExecutions()
  const { token } = useAuth()

  const fetchDash = async () => {
    try {
      const res = await fetch(`${API_BASE}/api/dashboard/admin`, {
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (res.ok) setDashData(await res.json())
    } catch (err) { console.warn('Dashboard API unavailable:', err) }
    finally { setLoading(false) }
  }

  useEffect(() => {
    fetchDash()
    const interval = setInterval(fetchDash, 10000)
    return () => clearInterval(interval)
  }, [])

  // Post a circuit breaker decision and refresh dashboard
  const handleCbDecision = async (decision: 'continue' | 'stop_for_day') => {
    if (!token || cbSubmitting) return
    setCbSubmitting(true)
    try {
      const res = await fetch(`${API_BASE}/api/circuit-breaker/decision`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ decision }),
      })
      if (res.ok) {
        addToast(
          decision === 'continue'
            ? '⚡ CB reset — continuing with new entries.'
            : '🛑 Stopped for the day — all strategies paused.',
          decision === 'continue' ? 'info' : 'warning',
          7000,
        )
        await fetchDash()
      } else {
        const err = await res.json().catch(() => ({ error: 'Failed' }))
        addToast(`CB decision failed: ${err.error ?? 'Unknown'}`, 'error', 5000)
      }
    } catch {
      addToast('CB decision request failed — check connection.', 'error', 5000)
    } finally {
      setCbSubmitting(false)
    }
  }

  const kpi = dashData?.kpi
  const stratMtm: Record<string, number> = kpi?.strategyMtm ?? {}
  const runningExecs: any[] = dashData?.runningExecutions ?? []
  const brokerPositions: any[] = dashData?.brokerPositions ?? []
  const traderPositions: any[] = dashData?.traderPositions ?? []
  const cb = dashData?.circuitBreaker ?? null  // { tripped, adminDecision, combinedPnl, lossPercent, threshold }

  // Derive filter options from live data
  const brokers = brokerPositions.map((b: any) => b.broker)
  const users = traderPositions.map((t: any) => t.trader)

  // Determine CB card state
  const cbState: 'safe' | 'tripped' | 'stopped' =
    !cb || !cb.tripped ? 'safe'
    : cb.adminDecision === 'stop_for_day' ? 'stopped'
    : 'tripped'

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '1rem', gap: '1.5rem' }}>
        <div className="page-head" style={{ marginBottom: 0 }}>
          <h1>Admin Dashboard</h1>
          <p className="page-sub">System overview, broker-wise positions, MTM &amp; panic controls</p>
        </div>
      </div>

      {/* ─── Filters + Panic Row ─── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem', gap: '1rem' }}>
        <div className="dash-filters" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
          <div className="filter-group">
            <label>User</label>
            <select className="cfg-input" value={selectedUser} onChange={(e) => setSelectedUser(e.target.value)}>
              <option value="all">All Users</option>
              {users.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="filter-group">
            <label>Broker</label>
            <select className="cfg-input" value={selectedBroker} onChange={(e) => setSelectedBroker(e.target.value)}>
              <option value="all">All Brokers</option>
              {brokers.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
        </div>
        <button className="btn btn-red btn-sm" onClick={() => setPanic({ level: 'all', targetName: 'ALL POSITIONS' })}>
          ⚠ Panic All
        </button>
      </div>

      {/* ─── Loading indicator ─── */}
      {loading && (
        <div style={{ textAlign: 'center', padding: '2rem', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
          <span style={{ animation: 'pulse 1.5s ease-in-out infinite' }}>⏳ Loading dashboard data…</span>
        </div>
      )}

      {/* ─── Unified KPI Strip (single source of truth) ─── */}
      <DesktopOnly>
      <div className="kpi-strip">
        <div className="kpi-card">
          <div className="kpi-label">Total MTM <InfoTip term="totalMtm" /></div>
          <div className={`kpi-value ${(kpi?.totalMtm ?? 0) >= 0 ? 'kpi-positive' : 'kpi-negative'}`}>{fmtINR(kpi?.totalMtm ?? 0)}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Active Traders</div>
          <div className="kpi-value">{traderPositions.length}</div>
          <div className="kpi-sub">across all brokers</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Strategies Running <InfoTip term="running" /></div>
          <div className="kpi-value">{kpi?.strategiesRunning ?? 0}</div>
          <div className="kpi-sub">{Object.keys(stratMtm).join(' + ') || '—'}</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Total Sets <InfoTip term="totalSets" /></div>
          <div className="kpi-value">{kpi?.totalSets ?? 0}</div>
          <div className="kpi-sub">{kpi?.activeSets ?? 0} active</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Win / Loss</div>
          <div className="kpi-value">{kpi?.wins ?? 0}W / {kpi?.losses ?? 0}L</div>
          <div className="kpi-sub">{kpi?.totalTrades ?? 0} total trades</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label">Strategy MTM <InfoTip term="strategyMtm" /></div>
          <div className="kpi-sub" style={{ marginTop: '0.25rem' }}>
            {Object.entries(stratMtm).map(([name, mtm]) => (
              <span key={name} style={{ color: (mtm as number) >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, display: 'block' }}>
                {name}: {fmtINR(mtm as number)}
              </span>
            ))}
            {Object.keys(stratMtm).length === 0 && <span style={{ color: 'var(--text-muted)' }}>—</span>}
          </div>
        </div>

        {/* ─── Circuit Breaker KPI Card ─── */}
        <div
          className="kpi-card"
          style={{
            borderColor:
              cbState === 'safe'
                ? 'rgba(34,197,94,0.25)'
                : cbState === 'stopped'
                ? 'rgba(239,68,68,0.35)'
                : 'rgba(239,68,68,0.55)',
            background:
              cbState === 'safe'
                ? 'rgba(34,197,94,0.04)'
                : 'rgba(239,68,68,0.06)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          {/* Animated top stripe when tripped */}
          {cbState === 'tripped' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: 2,
                background: 'linear-gradient(90deg, var(--red), #dc2626, var(--red))',
                backgroundSize: '200% 100%',
                animation: 'pulse 1.5s ease-in-out infinite',
              }}
            />
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.375rem' }}>
            {/* Status dot */}
            <div
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background:
                  cbState === 'safe' ? 'var(--green)'
                  : 'var(--red)',
                flexShrink: 0,
                boxShadow:
                  cbState === 'tripped'
                    ? '0 0 0 0 rgba(239,68,68,0.7)'
                    : 'none',
                animation:
                  cbState === 'tripped'
                    ? 'pulse 1.2s ease-out infinite'
                    : 'none',
              }}
            />
            <div className="kpi-label" style={{ marginBottom: 0 }}>
              Circuit Breaker
            </div>
          </div>

          {cbState === 'safe' && (
            <>
              <div className="kpi-value kpi-positive" style={{ fontSize: '1rem' }}>SAFE</div>
              <div className="kpi-sub">
                {cb
                  ? `${fmtINR(cb.combinedPnl ?? 0)} — ${(cb.lossPercent ?? 0).toFixed(2)}% of ${cb.threshold ?? 3}%`
                  : 'All systems normal'}
              </div>
            </>
          )}

          {cbState === 'tripped' && (
            <>
              <div
                className="kpi-value kpi-negative"
                style={{ fontSize: '0.875rem', fontWeight: 800, letterSpacing: '0.02em' }}
              >
                ⚡ TRIPPED
              </div>
              <div className="kpi-sub" style={{ color: 'var(--red)', marginBottom: '0.625rem' }}>
                {fmtINR(cb?.combinedPnl ?? 0)} — {(cb?.lossPercent ?? 0).toFixed(2)}% / {cb?.threshold ?? 3}%
              </div>
              <div style={{ display: 'flex', gap: '0.375rem', flexWrap: 'wrap' }}>
                <button
                  className="btn btn-sm"
                  disabled={cbSubmitting}
                  onClick={() => handleCbDecision('continue')}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    fontSize: '0.625rem',
                    padding: '0.3rem 0.5rem',
                    background: 'rgba(56,189,248,0.1)',
                    border: '1px solid rgba(56,189,248,0.35)',
                    color: 'var(--accent)',
                    opacity: cbSubmitting ? 0.5 : 1,
                    cursor: cbSubmitting ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  ⚡ Continue
                </button>
                <button
                  className="btn btn-sm"
                  disabled={cbSubmitting}
                  onClick={() => handleCbDecision('stop_for_day')}
                  style={{
                    flex: 1,
                    justifyContent: 'center',
                    fontSize: '0.625rem',
                    padding: '0.3rem 0.5rem',
                    background: 'rgba(239,68,68,0.1)',
                    border: '1px solid rgba(239,68,68,0.4)',
                    color: 'var(--red)',
                    opacity: cbSubmitting ? 0.5 : 1,
                    cursor: cbSubmitting ? 'not-allowed' : 'pointer',
                    whiteSpace: 'nowrap',
                  }}
                >
                  🛑 Stop
                </button>
              </div>
            </>
          )}

          {cbState === 'stopped' && (
            <>
              <div className="kpi-value kpi-negative" style={{ fontSize: '0.9375rem' }}>STOPPED</div>
              <div className="kpi-sub" style={{ color: 'var(--red)' }}>Stopped for the day</div>
            </>
          )}
        </div>
      </div>
      </DesktopOnly>

      <MobileOnly>
        <div className="m-kpi-row">
          <MobileKpi
            icon="💰"
            label="Total MTM"
            value={fmtINR(kpi?.totalMtm ?? 0)}
            accent={(kpi?.totalMtm ?? 0) >= 0 ? 'green' : 'red'}
          />
          <MobileKpi
            icon="👥"
            label="Traders"
            value={String(traderPositions.length)}
            subtitle="active"
          />
          <MobileKpi
            icon="▶"
            label="Strategies"
            value={String(kpi?.strategiesRunning ?? 0)}
            subtitle={Object.keys(stratMtm).join(', ') || '—'}
          />
          <MobileKpi
            icon="📦"
            label="Sets"
            value={String(kpi?.totalSets ?? 0)}
            subtitle={`${kpi?.activeSets ?? 0} active`}
          />
          <MobileKpi
            icon="📊"
            label="W / L"
            value={`${kpi?.wins ?? 0}W / ${kpi?.losses ?? 0}L`}
            subtitle={`${kpi?.totalTrades ?? 0} trades`}
          />
          <MobileKpi
            icon={cbState === 'safe' ? '🟢' : '🔴'}
            label="Circuit Breaker"
            value={cbState === 'safe' ? 'SAFE' : cbState === 'tripped' ? 'TRIPPED' : 'STOPPED'}
            accent={cbState === 'safe' ? 'green' : 'red'}
            subtitle={
              cb
                ? cbState === 'safe'
                  ? `${(cb.lossPercent ?? 0).toFixed(2)}% of ${cb.threshold ?? 3}%`
                  : `${fmtINR(cb.combinedPnl ?? 0)}`
                : '—'
            }
          />
        </div>
      </MobileOnly>


      {/* ─── Broker + Strategy tables side by side ─── */}
      <DesktopOnly>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
        {/* Broker-wise Positions */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">⚯ Broker Positions <InfoTip term="brokers" /></span>
          </div>
          <table>
            <thead>
              <tr>
                <th>Broker</th>
                <th>Sets <InfoTip term="totalSets" /></th>
                <th>MTM <InfoTip term="mtm" /></th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {brokerPositions.length > 0 ? brokerPositions.map((bp: any) => (
                <tr key={bp.broker}>
                  <td><strong>{bp.broker}</strong></td>
                  <td>{bp.sets ?? 0}</td>
                  <td><span className={`stat-value ${(bp.mtm ?? 0) >= 0 ? 'green' : 'red'}`}>{bp.mtm != null ? fmtINR(bp.mtm) : '—'}</span></td>
                  <td><span className={`badge ${bp.status === 'connected' ? 'badge-target' : 'badge-sl'}`}>{bp.status === 'connected' ? 'Connected' : 'Disconnected'}</span></td>
                  <td>
                    <button className="btn btn-sm btn-ghost">View</button>
                    {bp.status === 'connected' && (
                      <button className="btn btn-sm btn-red" style={{ marginLeft: '0.25rem' }}
                        onClick={() => setPanic({ level: 'broker', targetName: bp.broker })}>Panic</button>
                    )}
                    {bp.status !== 'connected' && (
                      <button className="btn btn-sm btn-ghost" style={{ marginLeft: '0.25rem' }}>Reconnect</button>
                    )}
                  </td>
                </tr>
              )) : (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No broker data available</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Strategy-wise MTM */}
        <div className="card">
          <div className="card-header">
            <span className="card-title">◈ Strategy MTM <InfoTip term="strategyMtm" /></span>
            <div style={{ display: 'flex', gap: '0.375rem' }}>
              {Object.keys(stratMtm).map(name => (
                <button key={name} className="btn btn-sm btn-red" onClick={() => setPanic({ level: 'strategy', targetName: name })}>
                  Panic {name}
                </button>
              ))}
            </div>
          </div>
          <table>
            <thead>
              <tr><th>Strategy</th><th>MTM <InfoTip term="mtm" /></th></tr>
            </thead>
            <tbody>
              {Object.entries(stratMtm).length > 0 ? Object.entries(stratMtm).map(([name, mtm]) => (
                <tr key={name}>
                  <td><strong>{name}</strong></td>
                  <td><span className={`stat-value ${(mtm as number) >= 0 ? 'green' : 'red'}`}>{fmtINR(mtm as number)}</span></td>
                </tr>
              )) : (
                <tr><td colSpan={2} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No strategy data</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </DesktopOnly>

      <MobileOnly>
        {/* Mobile Broker Positions */}
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>⚯ Broker Positions</h3>
          <div className="m-card-list">
            {brokerPositions.length > 0 ? brokerPositions.map((bp: any) => (
              <MobileCard
                key={bp.broker}
                title={bp.broker}
                status={{
                  label: bp.status === 'connected' ? 'Connected' : 'Disconnected',
                  variant: bp.status === 'connected' ? 'green' : 'red',
                }}
                fields={[
                  { label: 'Sets', value: bp.sets ?? 0 },
                  { label: 'MTM', value: bp.mtm != null ? fmtINR(bp.mtm) : '—', accent: (bp.mtm ?? 0) >= 0 ? 'green' : 'red', bold: true },
                ]}
                actions={[
                  { label: 'View', variant: 'ghost', onClick: () => {} },
                  ...(bp.status === 'connected'
                    ? [{ label: '⚠ Panic', variant: 'danger' as const, onClick: () => setPanic({ level: 'broker' as const, targetName: bp.broker }) }]
                    : [{ label: 'Reconnect', variant: 'ghost' as const, onClick: () => {} }]),
                ]}
              />
            )) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem', fontSize: '0.8125rem' }}>No broker data available</div>
            )}
          </div>
        </div>

        {/* Mobile Strategy MTM */}
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>◈ Strategy MTM</h3>
          <div className="m-card-list">
            {Object.entries(stratMtm).length > 0 ? Object.entries(stratMtm).map(([name, mtm]) => (
              <MobileCard
                key={name}
                title={name}
                fields={[
                  { label: 'MTM', value: fmtINR(mtm as number), accent: (mtm as number) >= 0 ? 'green' : 'red', bold: true },
                ]}
                actions={[
                  { label: '⚠ Panic', variant: 'danger', onClick: () => setPanic({ level: 'strategy', targetName: name }) },
                ]}
              />
            )) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem', fontSize: '0.8125rem' }}>No strategy data</div>
            )}
          </div>
        </div>
      </MobileOnly>

      {/* ─── Trader Positions ─── */}
      <DesktopOnly>
      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-header">
          <span className="card-title">👤 Trader Positions <InfoTip term="userManagement" /></span>
        </div>
        <table>
          <thead>
            <tr>
              <th>Trader</th>
              <th>Broker</th>
              <th>Sets <InfoTip term="totalSets" /></th>
              <th>Running</th>
              <th>MTM <InfoTip term="mtm" /></th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {traderPositions.length > 0 ? traderPositions.map((tp: any) => (
              <tr key={tp.trader}>
                <td><strong>{tp.trader}</strong></td>
                <td>{tp.broker ?? '—'}</td>
                <td>{tp.sets ?? 0}</td>
                <td>
                  {(tp.running ?? 0) > 0
                    ? <span className="badge badge-target">{tp.running} Running</span>
                    : <span className="badge badge-sl">Inactive</span>
                  }
                </td>
                <td><span className={`stat-value ${(tp.mtm ?? 0) >= 0 ? 'green' : 'red'}`}>{fmtINR(tp.mtm ?? 0)}</span></td>
                <td>
                  <button className="btn btn-sm btn-ghost" onClick={() => onNavigate('admin-executions')}>View</button>
                  {(tp.running ?? 0) > 0 && (
                    <button className="btn btn-sm btn-red" style={{ marginLeft: '0.25rem' }}
                      onClick={() => setPanic({ level: 'trader', targetName: tp.trader })}>Panic</button>
                  )}
                </td>
              </tr>
            )) : (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No trader data available</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </DesktopOnly>

      <MobileOnly>
        <div style={{ marginBottom: '1rem' }}>
          <h3 style={{ fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text-secondary)' }}>👤 Trader Positions</h3>
          <div className="m-card-list">
            {traderPositions.length > 0 ? traderPositions.map((tp: any) => (
              <MobileCard
                key={tp.trader}
                title={tp.trader}
                subtitle={tp.broker ?? '—'}
                status={{
                  label: (tp.running ?? 0) > 0 ? `${tp.running} Running` : 'Inactive',
                  variant: (tp.running ?? 0) > 0 ? 'green' : 'muted',
                }}
                fields={[
                  { label: 'Sets', value: tp.sets ?? 0 },
                  { label: 'MTM', value: fmtINR(tp.mtm ?? 0), accent: (tp.mtm ?? 0) >= 0 ? 'green' : 'red', bold: true },
                ]}
                actions={[
                  { label: 'View', variant: 'ghost', onClick: () => onNavigate('admin-executions') },
                  ...((tp.running ?? 0) > 0
                    ? [{ label: '⚠ Panic', variant: 'danger' as const, onClick: () => setPanic({ level: 'trader' as const, targetName: tp.trader }) }]
                    : []),
                ]}
              />
            )) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem', fontSize: '0.8125rem' }}>No trader data available</div>
            )}
          </div>
        </div>
      </MobileOnly>

      {/* All Executions Table */}
      <DesktopOnly>
      <div className="card">
        <div className="card-header">
          <span className="card-title">▶ All Running Executions <InfoTip term="executionSet" /></span>
          <button className="btn btn-sm btn-primary" onClick={() => onNavigate('admin-executions')}>View All</button>
        </div>
        <table>
          <thead>
            <tr><th>Set</th><th>Strategy</th><th>Trader</th><th>Underlying <InfoTip term="underlying" /></th><th>Status <InfoTip term="status" /></th><th>Actions</th></tr>
          </thead>
          <tbody>
            {runningExecs.length > 0 ? runningExecs.map((ex: any) => (
              <tr key={ex.id}>
                <td><strong>{ex.set_name ?? `Set ${ex.id}`}</strong></td>
                <td><code>{ex.strategy_type ?? '—'}</code></td>
                <td>{ex.trader_name ?? '—'}</td>
                <td>{ex.underlying ?? '—'}</td>
                <td><span className={`badge ${ex.status === 'running' ? 'badge-target' : 'badge-sl'}`}>{ex.status ?? '—'}</span></td>
                <td>
                  <button className="btn btn-sm btn-ghost">View</button>
                  {ex.status === 'running' && (
                    <button className="btn btn-sm btn-red" style={{ marginLeft: '0.25rem' }}
                      onClick={() => setPanic({ level: 'set', targetName: ex.set_name ?? `Set ${ex.id}` })}>X</button>
                  )}
                </td>
              </tr>
            )) : (
              <tr><td colSpan={6} style={{ textAlign: 'center', color: 'var(--text-muted)' }}>No running executions</td></tr>
            )}
          </tbody>
        </table>
      </div>
      </DesktopOnly>

      <MobileOnly>
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
            <h3 style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-secondary)' }}>▶ Running Executions</h3>
            <button className="btn btn-sm btn-primary" onClick={() => onNavigate('admin-executions')}>View All</button>
          </div>
          <div className="m-card-list">
            {runningExecs.length > 0 ? runningExecs.map((ex: any) => (
              <MobileCard
                key={ex.id}
                title={ex.set_name ?? `Set ${ex.id}`}
                subtitle={ex.trader_name ?? '—'}
                status={{
                  label: ex.status ?? '—',
                  variant: ex.status === 'running' ? 'green' : 'muted',
                }}
                fields={[
                  { label: 'Strategy', value: ex.strategy_type ?? '—' },
                  { label: 'Underlying', value: ex.underlying ?? '—' },
                ]}
                actions={[
                  { label: 'View', variant: 'ghost', onClick: () => {} },
                  ...(ex.status === 'running'
                    ? [{ label: '✕ Stop', variant: 'danger' as const, onClick: () => setPanic({ level: 'set' as const, targetName: ex.set_name ?? `Set ${ex.id}` }) }]
                    : []),
                ]}
              />
            )) : (
              <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1rem', fontSize: '0.8125rem' }}>No running executions</div>
            )}
          </div>
        </div>
      </MobileOnly>

      {/* Panic Confirm Modal */}
      {panic && (
        <div className="modal-overlay" onClick={() => setPanic(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ color: 'var(--red)' }}>⚠ Panic Exit Confirmation</h3>
              <button className="modal-close" onClick={() => setPanic(null)}>✕</button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--text-secondary)' }}>
                You are about to square off <strong>{panic.level === 'all' ? 'ALL POSITIONS' : `all positions for ${panic.targetName}`}</strong> ({panic.level}-level).
              </p>
              <div style={{ background: 'var(--red-glow)', padding: '0.75rem', borderRadius: 'var(--radius-sm)', border: '1px solid rgba(239,68,68,0.2)', fontSize: '0.8125rem' }}>
                This action will immediately square off all open positions for the selected scope. This cannot be undone.
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={() => setPanic(null)}>Cancel</button>
              <button className="btn btn-red" onClick={() => {
                panicExit(panic.level as 'set' | 'strategy' | 'broker' | 'all', panic.level === 'all' ? undefined : panic.targetName)
                addToast(`Panic exit executed: ${panic.level} — ${panic.targetName}`, 'warning', 6000)
                setPanic(null)
              }}>
                ⚠ Confirm Panic Exit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
